import { Modal, Form, Select, InputNumber, Input, DatePicker, message } from "antd";
import { useUpdate, useGetIdentity } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import dayjs from "dayjs";
import type { Audit, AuditFormValues } from "../../../types/audit";
import type { UserInfo } from "../../../types/auth";
import { ADMIN_USER_TYPES, isEurosemillasScoped } from "../../../types/auth";

interface AuditEditModalProps {
  visible: boolean;
  onClose: () => void;
  audit: Audit | null;
  /** ID de la planning a la que pertenece la audit — necesario para
   *  cargar el listado de fincas asignables (endpoint scopeado). */
  planningId?: number;
  /** Legacy — sin uso ahora que la búsqueda de fincas va por
   *  `/planning-groups/{planningId}/assignable-farms/`. Se mantiene
   *  por compatibilidad con los call-sites existentes. */
  countryId?: number;
  onSuccess?: () => void;
}

/**
 * AuditEditModal Component
 * Modal for editing an existing audit
 */
export const AuditEditModal = ({
  visible,
  onClose,
  audit,
  planningId,
  // countryId — sin uso, ver interfaz.
  onSuccess,
}: AuditEditModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AuditFormValues>();
  const { mutate: updateAudit } = useUpdate();
  const { data: identity } = useGetIdentity<UserInfo>();
  const userType = identity?.app_user?.user_type ?? -1;
  const isAdmin =
    (ADMIN_USER_TYPES as readonly number[]).includes(userType)
    && !isEurosemillasScoped(identity);
  const isCoordinator = userType === 4;
  // Coordinator can reassign the auditor straight from the edit modal
  // (matches their day-to-day workflow). Admin too. Regular auditors
  // (type=5) only see planning_date.
  const canEditAuditor = isAdmin || isCoordinator;
  const [farms, setFarms] = useState<any[]>([]);
  const [loadingFarms, setLoadingFarms] = useState(false);
  const [auditTypes, setAuditTypes] = useState<any[]>([]);
  const [loadingAuditTypes, setLoadingAuditTypes] = useState(false);
  const [auditors, setAuditors] = useState<{ id: number; name: string; code?: string }[]>([]);
  const [loadingAuditors, setLoadingAuditors] = useState(false);
  const [auditDetail, setAuditDetail] = useState<any>(null);
  const [loadingAuditDetail, setLoadingAuditDetail] = useState(false);

  // Load audit detail when modal opens
  useEffect(() => {
    const loadAuditDetail = async () => {
      if (!visible || !audit?.id) return;
      
      setLoadingAuditDetail(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const response = await apiClient.get(`/audits/${audit.id}/`);
        
        console.log('📋 Audit detail loaded:', response.data);
        setAuditDetail(response.data);
      } catch (error) {
        console.error('Error loading audit detail:', error);
        message.error('Error al cargar los detalles de la auditoría');
      } finally {
        setLoadingAuditDetail(false);
      }
    };

    loadAuditDetail();
  }, [visible, audit?.id]);

  // Load assignable farms when the modal opens (admin-only — non-admins
  // can't change the farm and the field isn't rendered). Endpoint
  // scopeado por planning: filtra por país de la planning y excluye
  // fincas ya en OTRAS planificaciones de la misma campaña. Antes
  // usábamos `/farms/?country=X&page_size=10000` y el dropdown se
  // quedaba vacío cuando el prop `countryId` venía undefined.
  useEffect(() => {
    const loadFarms = async () => {
      if (!visible || !isAdmin || !planningId) return;

      setLoadingFarms(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const response = await apiClient.get(
          `/planning-groups/${planningId}/assignable-farms/`,
        );
        setFarms(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error loading farms:', error);
        message.error('Error al cargar las fincas');
        setFarms([]);
      } finally {
        setLoadingFarms(false);
      }
    };

    loadFarms();
  }, [visible, planningId, isAdmin]);

  // Load audit types when modal opens (admin-only — same reasoning
  // as farms).
  useEffect(() => {
    const loadAuditTypes = async () => {
      if (!visible || !isAdmin) return;

      setLoadingAuditTypes(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const response = await apiClient.get('/audit-types/?page_size=100');
        
        if (response.data.results) {
          setAuditTypes(response.data.results);
        } else if (Array.isArray(response.data)) {
          setAuditTypes(response.data);
        }
      } catch (error) {
        console.error('Error loading audit types:', error);
        message.error('Error al cargar los tipos de auditoría');
      } finally {
        setLoadingAuditTypes(false);
      }
    };

    loadAuditTypes();
  }, [visible, isAdmin]);

  // Load auditors only when the dropdown will actually render — i.e.
  // the current user is admin or coordinator. For a coordinator we
  // ideally would restrict to their company, but the legacy endpoint
  // doesn't take that filter and the admin UI uses the same call.
  useEffect(() => {
    if (!visible || !canEditAuditor) return;
    const loadAuditors = async () => {
      setLoadingAuditors(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const response = await apiClient.get(
          '/app-users/?user_type__in=4,5&page_size=500',
        );
        const list = response.data?.results ?? response.data?.data ?? response.data ?? [];
        setAuditors(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error loading auditors:', error);
      } finally {
        setLoadingAuditors(false);
      }
    };
    loadAuditors();
  }, [visible, canEditAuditor]);

  // Populate form when audit detail is loaded. Admins need farms +
  // auditTypes loaded to populate those selects; non-admins only see
  // the date (and optionally auditor for coordinator), so we don't
  // gate on those fetches finishing.
  useEffect(() => {
    if (!auditDetail || !visible) return;
    if (isAdmin && (farms.length === 0 || auditTypes.length === 0)) return;

    const base: Partial<AuditFormValues> = {};
    if (isAdmin) {
      Object.assign(base, {
        farm: auditDetail.farm?.id,
        audit_type: auditDetail.audit_type?.id,
        samples_to_take: auditDetail.samples_to_take,
        samples_to_process: auditDetail.samples_to_process,
        remark: auditDetail.remark,
      } as Partial<AuditFormValues>);
    }
    form.setFieldsValue({
      ...base,
      // The AuditFormValues type doesn't list planning_date / auditor_user
      // because they're conditional/admin-only — cast to bypass and rely on
      // runtime keys.
      ...({
        planning_date: auditDetail.planning_date
          ? dayjs(auditDetail.planning_date)
          : null,
        auditor_user: auditDetail.auditor_user?.id ?? auditDetail.auditor_user ?? null,
      } as Partial<AuditFormValues>),
    });
  }, [auditDetail, visible, farms, auditTypes, form, isAdmin]);

  const handleSubmit = (values: AuditFormValues & { planning_date?: any; auditor_user?: number | null }) => {
    if (!audit) return;

    // Format planning_date (DatePicker emits dayjs; backend expects YYYY-MM-DD).
    const payload: Record<string, any> = { ...values };
    if (payload.planning_date && typeof payload.planning_date === "object") {
      payload.planning_date = dayjs(payload.planning_date).format("YYYY-MM-DD");
    } else if (payload.planning_date === null || payload.planning_date === "") {
      payload.planning_date = null;
    }

    if (!isAdmin) {
      // Non-admins are only allowed to touch planning_date (everyone)
      // and auditor_user (coordinator only). Strip everything else to
      // avoid wiping fields the form didn't render with a stale value.
      const allowed = new Set<string>(["planning_date"]);
      if (canEditAuditor) allowed.add("auditor_user");
      for (const key of Object.keys(payload)) {
        if (!allowed.has(key)) delete payload[key];
      }
    }

    updateAudit(
      {
        resource: "audits",
        id: audit.id,
        values: payload,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        },
      }
    );
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("audit.edit")}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {isAdmin && (
          <>
            <Form.Item
              name="farm"
              label={t("audit.farm")}
              rules={[
                {
                  required: true,
                  message: `${t("audit.farm")} ${t("common.required")}`,
                },
              ]}
            >
              <Select
                placeholder={t(
                  "audit.farm_search_placeholder",
                  "Buscar por productor o finca…",
                )}
                showSearch
                allowClear
                loading={loadingFarms}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const hay = (option?.title as string | undefined) ?? "";
                  const needle = input
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/\p{Diacritic}/gu, "");
                  return hay.includes(needle);
                }}
                options={farms.map((farm: any) => {
                  const producer = farm.producer_name || "—";
                  const town = farm.town_name || "";
                  const province = farm.province_name || "";
                  const location = town && province ? `${town} (${province})` : town || province;
                  const label = `${producer} · ${farm.name}${location ? ` — ${location}` : ""}`;
                  const searchText = `${producer} ${farm.name} ${town} ${province}`
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/\p{Diacritic}/gu, "");
                  return {
                    value: farm.id,
                    label,
                    title: searchText,
                  };
                })}
              />
            </Form.Item>

            <Form.Item
              name="audit_type"
              label={t("audit.type")}
              rules={[
                {
                  required: true,
                  message: `${t("audit.type")} ${t("common.required")}`,
                },
              ]}
            >
              <Select
                loading={loadingAuditTypes}
                placeholder={`${t("common.select")} ${t("audit.type")}`}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                options={auditTypes.map((type) => ({
                  label: type.name,
                  value: type.id,
                }))}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="planning_date"
          label={t("audit.planning_date", "Fecha planificada")}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={t("audit.planning_date_placeholder", "Selecciona la fecha en que se hará la auditoría")}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
        </Form.Item>

        {canEditAuditor && (
          <Form.Item
            name="auditor_user"
            label={t("audit.auditor", "Auditor")}
          >
            <Select
              showSearch
              allowClear
              loading={loadingAuditors}
              placeholder={t("audit.assign_auditor", "Asignar auditor")}
              optionFilterProp="label"
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              options={auditors.map((a) => ({
                value: a.id,
                label: `${a.name}${a.code ? ` (${a.code})` : ""}`,
              }))}
            />
          </Form.Item>
        )}

        {isAdmin && (
          <>
            <Form.Item
              name="samples_to_take"
              label={t("audit.samples_to_take")}
              rules={[
                {
                  required: true,
                  message: `${t("audit.samples_to_take")} ${t("common.required")}`,
                },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={t("audit.samples_to_take")}
              />
            </Form.Item>

            <Form.Item
              name="samples_to_process"
              label={t("audit.samples_to_process")}
              rules={[
                {
                  required: true,
                  message: `${t("audit.samples_to_process")} ${t("common.required")}`,
                },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={t("audit.samples_to_process")}
              />
            </Form.Item>

            <Form.Item name="remark" label={t("audit.remark")}>
              <Input.TextArea
                rows={4}
                placeholder={t("audit.remark_placeholder")}
                maxLength={500}
                showCount
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};
